import React, { FormEvent, useContext, useEffect, useState } from 'react';
import { FaEye, FaLock, FaUser } from 'react-icons/fa';
import styled from '../theme';
import { Logo } from '../components/auth/logo';
import { AuthHeader } from '../components/auth/auth_header';
import Button from '../ui_components/button/button';
import FormField from '../ui_components/form/formField';
import ValidationForm from '../ui_components/validation/validationForm';
import { ValidationPopupInput } from '../ui_components/validation/validationPopupInput';
import { ApplicationContext } from '../ApplicationContext';
import { emailVerification, setupProfile } from '../api/signup';
import { loginHandle } from '../api/signin';
import {
  clearCachedPricingPermission,
  writeCachedPricingPermission,
} from '../helper/pricingPermission';

const TEAM_SETTINGS_ACCESS_CACHE_PREFIX = 'team-settings-access-';
const TEAM_SETTINGS_PLAN_ACCESS_CACHE_PREFIX = 'team-settings-plan-access-';

const LeftIconInput = styled(ValidationPopupInput)`
  flex-direction: row-reverse;
  position: relative;

  .leading-icon {
    margin-right: 0.5em;
    flex-shrink: 0;
    color: #4b5563;
  }

  .toggle-icon {
    position: absolute;
    top: 50%;
    right: 0.85em;
    transform: translateY(-50%);
    cursor: pointer;
    color: #4b5563;
  }

  &.eye > .input {
    padding-right: 1.5em;
  }
`;

const SetupForm = styled(ValidationForm)`
  padding: 0 1em;
`;

const SetupMessage = styled.div`
  margin: 0.75em 1em;
  font-size: 0.92em;
  color: #d14343;
`;

const PASSWORD_PATTERN =
  '(?=^.{8,}$)((?=.*\\d)|(?=.*\\W+))(?![.\\n])(?=.*[A-Z])(?=.*[a-z]).*$';

const toDisplayWord = (word: string): string =>
  word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '';

const buildAutoLabel = (firstName: string, lastName: string): string => {
  const words = `${firstName} ${lastName}`.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '';
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return words
    .slice(0, 4)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
};

const normalizeLabelInput = (value: string): string =>
  String(value || '')
    .replace(/^@+/, '')
    .replace(/\s+/g, '')
    .toUpperCase()
    .slice(0, 4);

const getTokenFromQuery = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const params = new URLSearchParams(window.location.search || '');
  return String(params.get('token') || '').trim();
};

const isInviteAcceptedNavigation = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search || '');
  return params.get('inviteAccepted') === '1';
};

const clearTeamSettingsAccessCache = (): void => {
  try {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (
        key &&
        (key.startsWith(TEAM_SETTINGS_ACCESS_CACHE_PREFIX) ||
          key.startsWith(TEAM_SETTINGS_PLAN_ACCESS_CACHE_PREFIX))
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    // Ignore cache cleanup failures.
  }
};

const clearSessionForInviteAcceptance = (
  currentUser: any,
  setUser: (nextUser: any) => void
): void => {
  const email = String(currentUser?.user?.email || '').trim();
  const accountId = localStorage.getItem('accountId');
  if (accountId) {
    localStorage.setItem(`logout-event-${accountId}`, `logout${Math.random()}`);
  }

  clearTeamSettingsAccessCache();

  const setOnlyEmail = {
    user: {
      email,
    },
  };

  localStorage.setItem('quipa-store', JSON.stringify(setOnlyEmail));
  localStorage.removeItem('accountId');
  localStorage.removeItem('ownerAccountId');
  localStorage.removeItem('subaccountCanSeePricing');
  clearCachedPricingPermission();
  localStorage.removeItem('showModal');
  localStorage.removeItem('firstName');
  localStorage.removeItem('seed-other-item');
  localStorage.removeItem('seed-other-items');
  localStorage.removeItem('seed-otheritems');
  localStorage.removeItem('lastName');
  localStorage.removeItem('role');
  localStorage.removeItem('avatarId');
  localStorage.removeItem('logoId');
  localStorage.removeItem('status');
  localStorage.removeItem('isBeta');
  localStorage.removeItem('appLoaded');
  localStorage.removeItem('createdAt');
  localStorage.removeItem('customerId');
  sessionStorage.removeItem('lastVisit');

  document.cookie.split(';').forEach((item) => {
    const cookieName = item.trim().split('=')[0];
    if (cookieName.startsWith('crisp-client')) {
      const cookieDomain = '.quipa.com';
      const cookiePath = '/';

      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${cookiePath}; domain=${cookieDomain};`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${cookiePath};`;
    }
  });

  setUser(setOnlyEmail);
};

const ProfileSetupPage = (): JSX.Element => {
  const { user, setUser } = useContext(ApplicationContext);
  const [token] = useState<string>(getTokenFromQuery());
  const inviteAccepted = isInviteAcceptedNavigation();
  const [sessionPrepared, setSessionPrepared] = useState<boolean>(() => !inviteAccepted);
  const [email, setEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [isLabelAuto, setIsLabelAuto] = useState<boolean>(true);
  const [isConvertedFromAssignee, setIsConvertedFromAssignee] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [emailResolved, setEmailResolved] = useState<boolean>(false);

  useEffect(() => {
    if (sessionPrepared) {
      return;
    }

    if (inviteAccepted && user?.user?.token) {
      clearSessionForInviteAcceptance(user, setUser);
    }

    setSessionPrepared(true);
  }, [inviteAccepted, sessionPrepared, setUser, user]);

  const resolveInviteEmail = async () => {
    if (emailResolved || isLoading) {
      return;
    }

    if (!token) {
      setErrorMessage('Invitation token is missing. Please use the invitation email link.');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');
      const response = await emailVerification(token);
      const resolvedEmail = String(response?.email || '').trim();
      if (!resolvedEmail) {
        throw new Error('Unable to resolve invitation email');
      }
      const resolvedLabel = normalizeLabelInput(String(response?.label || ''));
      const nextIsConvertedFromAssignee = response?.isConvertedFromAssignee === true;
      setEmail(resolvedEmail);
      setIsConvertedFromAssignee(nextIsConvertedFromAssignee);
      if (nextIsConvertedFromAssignee && resolvedLabel) {
        setLabel(resolvedLabel);
        setIsLabelAuto(false);
      } else {
        setLabel('');
        setIsLabelAuto(true);
      }
      setEmailResolved(true);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || 'Unable to validate invitation token. Please request a new invite.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionPrepared && !emailResolved && !isLoading && !errorMessage) {
      void resolveInviteEmail();
    }
  }, [sessionPrepared, emailResolved, isLoading, errorMessage, token]);

  useEffect(() => {
    if (isLabelAuto) {
      setLabel(buildAutoLabel(firstName, lastName));
    }
  }, [firstName, lastName, isLabelAuto]);

  const handleShowPassword = (id: string) => {
    const input = document.getElementById(id) as HTMLInputElement | null;
    if (!input) {
      return;
    }
    input.type = input.type === 'password' ? 'text' : 'password';
  };

  const persistLoginAndRedirect = async (resolvedEmail: string, resolvedPassword: string) => {
    const loginResponse = await loginHandle({
      email: resolvedEmail,
      password: resolvedPassword,
      remember: true,
    });

    if (loginResponse?.error) {
      throw new Error(loginResponse.message || 'Login failed');
    }

    const { account, user, token: jwtToken, refreshToken, crispAccessCookie } = loginResponse;
    const userObj = {
      email: user.email,
      token: jwtToken,
      refreshToken,
      item_order: '0',
      type: account.type,
      isBeta: account.isBeta,
    };

    setUser({ user: userObj });

    localStorage.setItem('quipa-store', JSON.stringify({ user: userObj }));
    localStorage.setItem('accountId', String(account.id));
    writeCachedPricingPermission(!!account.canSeePricing);
    if (Number(account.ownerAccountId) > 0) {
      localStorage.setItem('ownerAccountId', String(account.ownerAccountId));
      localStorage.setItem('subaccountCanSeePricing', account.canSeePricing ? '1' : '0');
    } else {
      localStorage.removeItem('ownerAccountId');
      localStorage.removeItem('subaccountCanSeePricing');
    }
    localStorage.setItem('firstName', String(user.firstName || ''));
    localStorage.setItem('lastName', String(user.lastName || ''));
    localStorage.setItem('role', String(user.role || ''));
    localStorage.setItem('avatarId', String(account.avatarId || ''));
    localStorage.setItem('status', String(account.type || ''));
    localStorage.setItem('isBeta', String(!!account.isBeta));
    localStorage.setItem('createdAt', String(account.createdAt || ''));

    if (user.logoId) {
      localStorage.setItem('logoId', String(user.logoId));
    } else {
      localStorage.removeItem('logoId');
    }

    if (account.customerId) {
      localStorage.setItem('customerId', String(account.customerId));
    }

    if (crispAccessCookie) {
      document.cookie = `access=${crispAccessCookie}`;
    }

    window.location.href = '/user_settings';
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!emailResolved || !email) {
      setErrorMessage('Invitation email is not resolved yet.');
      return;
    }

    const normalizedFirstName = toDisplayWord(firstName.trim());
    const normalizedLastName = toDisplayWord(lastName.trim());
    const normalizedLabel = normalizeLabelInput(label);
    const normalizedPassword = password.trim();
    const normalizedPasswordConfirm = passwordConfirm.trim();

    if (!normalizedFirstName || !normalizedLastName) {
      setErrorMessage('First and last name are required.');
      return;
    }

    if (!normalizedLabel) {
      setErrorMessage('Label could not be generated. Please enter first and last name.');
      return;
    }

    if (normalizedLabel.length > 4) {
      setErrorMessage('Label must be 1 to 4 characters.');
      return;
    }

    if (!normalizedPassword) {
      setErrorMessage('Password is required.');
      return;
    }

    if (!new RegExp(PASSWORD_PATTERN).test(normalizedPassword)) {
      setErrorMessage(
        'Password must contain at least 8 characters including uppercase, lowercase, and number or special character.'
      );
      return;
    }

    if (normalizedPassword !== normalizedPasswordConfirm) {
      setErrorMessage('Passwords must match.');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');

      await setupProfile({
        token,
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        label: normalizedLabel,
        password: normalizedPassword,
      });

      await persistLoginAndRedirect(email, normalizedPassword);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to complete profile setup. Please try again.'
      );
      setIsLoading(false);
    }
  };

  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
  };

  const handleLabelChange = (value: string) => {
    if (isConvertedFromAssignee) {
      return;
    }
    const nextLabel = normalizeLabelInput(value);
    setLabel(nextLabel);
    setIsLabelAuto(false);
  };

  return (
    <>
      <Logo />
      <AuthHeader uiSize="1.25em" style={{ margin: '0.7em 0px 1em', padding: '0px 0px 10px' }}>
        PROFILE SETUP
      </AuthHeader>

      {errorMessage ? <SetupMessage>{errorMessage}</SetupMessage> : null}

      <SetupForm
        uiOnSubmit={submit}
        uiPadding={false}
        uiSubmitFailedMessage="Profile setup failed"
        uiSubmitFailedIcon
        uiReportValidityMode="firstInvalid"
        uiDimmerBlurring={false}
      >
        <FormField uiRequired uiErrorAlignment="left">
          <LeftIconInput
            id="setup_email"
            name="email"
            type="email"
            required
            minLength={1}
            maxLength={255}
            value={email}
            placeholder="E-Mail"
            uiDisabled={true}
            uiReportOnBlur={false}
          >
            <FaUser className="leading-icon" />
          </LeftIconInput>
        </FormField>

        <FormField uiRequired uiErrorAlignment="left">
          <LeftIconInput
            id="setup_first_name"
            name="first_name"
            required
            minLength={1}
            maxLength={255}
            value={firstName}
            placeholder="First Name"
            uiReportOnBlur={false}
            onChange={(e) => handleFirstNameChange(String(e.currentTarget.value || ''))}
          >
            <FaUser className="leading-icon" />
          </LeftIconInput>
        </FormField>

        <FormField uiRequired uiErrorAlignment="left">
          <LeftIconInput
            id="setup_last_name"
            name="last_name"
            required
            minLength={1}
            maxLength={255}
            value={lastName}
            placeholder="Last Name"
            uiReportOnBlur={false}
            onChange={(e) => handleLastNameChange(String(e.currentTarget.value || ''))}
          >
            <FaUser className="leading-icon" />
          </LeftIconInput>
        </FormField>

        <FormField uiRequired uiErrorAlignment="left">
          <LeftIconInput
            id="setup_label"
            name="label"
            required
            minLength={1}
            maxLength={4}
            value={label}
            placeholder="Label"
            uiDisabled={isConvertedFromAssignee}
            uiReportOnBlur={false}
            onChange={(e) => handleLabelChange(String(e.currentTarget.value || ''))}
          >
            <FaUser className="leading-icon" />
          </LeftIconInput>
        </FormField>

        <FormField uiRequired uiErrorAlignment="left">
          <LeftIconInput
            id="setup_password"
            name="password"
            type="password"
            required
            minLength={8}
            maxLength={255}
            pattern={PASSWORD_PATTERN}
            placeholder="Password"
            uiHideOnClickOutside={true}
            uiReportOnBlur={true}
            className="eye"
            onChange={(e) => setPassword(String(e.currentTarget.value || ''))}
          >
            <FaEye
              className="toggle-icon"
              onClick={() => handleShowPassword('setup_password')}
            />
            <FaLock className="leading-icon" />
          </LeftIconInput>
        </FormField>

        <FormField uiRequired uiErrorAlignment="left">
          <LeftIconInput
            id="setup_password_repeat"
            name="password_repeat"
            type="password"
            required
            minLength={8}
            maxLength={255}
            pattern={PASSWORD_PATTERN}
            placeholder="Confirm Password"
            uiHideOnClickOutside={true}
            uiReportOnBlur={true}
            className="eye"
            onChange={(e) => setPasswordConfirm(String(e.currentTarget.value || ''))}
          >
            <FaEye
              className="toggle-icon"
              onClick={() => handleShowPassword('setup_password_repeat')}
            />
            <FaLock className="leading-icon" />
          </LeftIconInput>
        </FormField>

        <Button
          uiColor="primary"
          type="submit"
          uiDisabled={isLoading || !emailResolved}
          disabled={isLoading || !emailResolved}
        >
          {isLoading ? 'SETTING UP...' : 'COMPLETE SETUP'}
        </Button>
      </SetupForm>
    </>
  );
};

export { ProfileSetupPage };
